import { useState, useRef, useEffect, useCallback } from 'react';
import AccessibleModal from './AccessibleModal';
import {
  Loader2, Paperclip, X, FileText,
  Image as ImageIcon, Music, File, Plus, Trash2, Edit3,
  MessageSquare, Square, Copy, CheckCheck,
  Search, ArrowUp, PanelLeft, ArrowLeft, AlertCircle, Brain,
  Mic, MicOff, Phone, Zap, ChevronDown, ChevronUp, CheckCircle, Download, Mail,
} from 'lucide-react';
import VoiceMode from './VoiceMode';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useChat } from '../contexts/ChatContext';
import { useReplica } from '../contexts/ReplicaContext';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../lib/env';
import type { ChatAttachment, Conversation, WorkflowMetaData } from '../types';
import HRLogo, { type HRLogoState } from './HRLogo';
import ChatToolbar from './ChatToolbar';
import { useChatPreferences, getModelTier } from '../config/chat-preferences';

const API_BASE = getApiUrl();

// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType.startsWith('audio/')) return Music;
  if (mimeType === 'application/pdf' || mimeType.startsWith('text/')) return FileText;
  return File;
}

const ALLOWED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'audio/webm', 'audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/ogg',
  'application/pdf', 'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

interface PendingFile { file: File; preview?: string; }

/** Group conversations by relative date */
function groupConversations(convos: Conversation[]): { label: string; items: Conversation[] }[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const sevenDaysAgo = new Date(today.getTime() - 7 * 86400000);
  const groups: Record<string, Conversation[]> = { Today: [], Yesterday: [], 'Previous 7 Days': [], Older: [] };
  for (const c of convos) {
    const d = new Date(c.lastMessageAt || c.createdAt);
    if (d >= today) groups.Today.push(c);
    else if (d >= yesterday) groups.Yesterday.push(c);
    else if (d >= sevenDaysAgo) groups['Previous 7 Days'].push(c);
    else groups.Older.push(c);
  }
  return Object.entries(groups).filter(([, items]) => items.length > 0).map(([label, items]) => ({ label, items }));
}

// ═══════════════════════════════════════════════════════════════════════════════
// CODE BLOCK — dark rounded panel with copy button (ChatGPT style)
// ═══════════════════════════════════════════════════════════════════════════════

function CodeBlock({ language, children }: { language?: string; children: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(children); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div className="relative group my-4 rounded-xl overflow-hidden bg-[#0d0d0d] border border-white/[0.06]">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 text-[11px] text-[#b4b4b4] bg-[#161616] border-b border-white/[0.06]">
        <span className="font-medium">{language || 'code'}</span>
        <button onClick={handleCopy} className="flex items-center gap-1 hover:text-white transition-colors">
          {copied ? <CheckCheck size={12} /> : <Copy size={12} />}
          <span>{copied ? 'Copied!' : 'Copy code'}</span>
        </button>
      </div>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneDark}
        customStyle={{ margin: 0, padding: '16px 20px', borderRadius: 0, fontSize: '13px', lineHeight: '1.6', background: '#0d0d0d' }}
        wrapLongLines
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MARKDOWN RENDERER
// ═══════════════════════════════════════════════════════════════════════════════

function MarkdownMessage({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        code({ className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          const codeString = String(children).replace(/\n$/, '');
          if (match) return <CodeBlock language={match[1]}>{codeString}</CodeBlock>;
          return <code className="px-1.5 py-0.5 rounded bg-chat-code-bg text-chat-code-text text-[13px] font-mono" {...props}>{children}</code>;
        },
        pre({ children }) { return <>{children}</>; },
        p({ children }) { return <p className="mb-3 last:mb-0 leading-[1.7]">{children}</p>; },
        ul({ children }) { return <ul className="list-disc pl-6 mb-3 space-y-1.5">{children}</ul>; },
        ol({ children }) { return <ol className="list-decimal pl-6 mb-3 space-y-1.5">{children}</ol>; },
        li({ children }) { return <li className="leading-[1.7]">{children}</li>; },
        h1({ children }) { return <h1 className="text-[22px] font-semibold mb-4 mt-6 first:mt-0">{children}</h1>; },
        h2({ children }) { return <h2 className="text-[18px] font-semibold mb-3 mt-5 first:mt-0">{children}</h2>; },
        h3({ children }) { return <h3 className="text-[16px] font-semibold mb-2 mt-4 first:mt-0">{children}</h3>; },
        blockquote({ children }) {
          return <blockquote className="border-l-2 border-hig-text-tertiary pl-4 my-3 text-hig-text-secondary italic">{children}</blockquote>;
        },
        table({ children }) {
          return <div className="overflow-x-auto my-4 rounded-xl border border-chat-border-subtle"><table className="min-w-full text-sm">{children}</table></div>;
        },
        th({ children }) { return <th className="px-4 py-2.5 text-left text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider bg-chat-card border-b border-chat-border-subtle">{children}</th>; },
        td({ children }) { return <td className="px-4 py-2.5 border-b border-chat-border-subtle">{children}</td>; },
        a({ href, children }) { return <a href={href} target="_blank" rel="noopener noreferrer" className="text-hig-accent hover:underline">{children}</a>; },
        strong({ children }) { return <strong className="font-semibold">{children}</strong>; },
        hr() { return <hr className="my-6 border-chat-border-subtle" />; },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DELETE CONFIRMATION DIALOG
// ═══════════════════════════════════════════════════════════════════════════════

function DeleteConfirmDialog({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <AccessibleModal
      open={true}
      onClose={onCancel}
      title="Delete conversation"
      backdropClassName="bg-black/50 z-[60]"
      panelClassName="bg-chat-panel border border-chat-border rounded-2xl p-6 max-w-sm mx-4 shadow-2xl"
    >
      <h3 className="text-hig-text-primary font-semibold text-[15px] mb-2">Delete conversation?</h3>
      <p className="text-hig-text-tertiary text-[13px] mb-5 leading-relaxed">This will permanently delete this conversation and all its messages. This action cannot be undone.</p>
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-[13px] text-hig-text-secondary hover:text-hig-text-primary rounded-lg hover:bg-chat-hover transition-colors">Cancel</button>
        <button onClick={onConfirm} className="px-4 py-2 text-[13px] text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors">Delete</button>
      </div>
    </AccessibleModal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// WORKFLOW RESULT CARD — renders inline workflow artifacts
// ═══════════════════════════════════════════════════════════════════════════════

function WorkflowResultCard({ meta }: { meta: WorkflowMetaData }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const statusStyles: Record<string, { bg: string; text: string; label: string }> = {
    completed: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Completed' },
    running: { bg: 'bg-hig-accent/10', text: 'text-hig-accent', label: 'Running…' },
    failed: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Failed' },
    error: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Error' },
    needs_approval: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Needs Approval' },
  };
  const st = statusStyles[meta.status] || statusStyles.completed;

  return (
    <div className="mt-3 rounded-xl border border-white/[0.08] overflow-hidden" style={{ background: '#1a1a1a' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
          <Zap size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-hig-text-primary truncate">{meta.templateTitle}</p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${st.bg} ${st.text}`}>
          {st.label}
        </span>
      </div>

      {/* Artifacts */}
      {(meta.artifacts || []).length > 0 && (
        <div className="divide-y divide-chat-border-subtle">
          {(meta.artifacts || []).map(artifact => {
            const isExpanded = expanded[artifact.id];
            const preview = artifact.content.slice(0, 300);
            const needsTruncation = artifact.content.length > 300;
            return (
              <div key={artifact.id} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={12} className="text-emerald-400" />
                    <span className="text-[12px] font-medium text-hig-text-secondary">{artifact.title}</span>
                  </div>
                  {needsTruncation && (
                    <button
                      onClick={() => setExpanded(prev => ({ ...prev, [artifact.id]: !prev[artifact.id] }))}
                      className="p-1 rounded text-hig-text-tertiary hover:text-hig-text-primary transition-colors"
                    >
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  )}
                </div>
                <div className="mt-2 text-[13px] leading-relaxed text-hig-text-secondary">
                  <MarkdownMessage content={isExpanded ? artifact.content : (needsTruncation ? preview + '…' : artifact.content)} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT RESULT CARD — renders generated document artifacts inline
// ═══════════════════════════════════════════════════════════════════════════════

function DocumentResultCard({ meta, onDraftReply }: { meta: any; onDraftReply?: (subject: string, from: string, messageId?: string) => void }) {

  const formatIcons: Record<string, string> = {
    pdf: '📄', pptx: '📊', xlsx: '📈', docx: '📝',
  };
  const formatLabels: Record<string, string> = {
    pdf: 'PDF Report', pptx: 'Presentation', xlsx: 'Spreadsheet', docx: 'Word Document',
  };

  const icon = formatIcons[meta.format] || '📄';
  const label = formatLabels[meta.format] || 'Document';
  const sections: Array<{ heading: string; content: string; emailMeta?: { messageId?: string; from?: string; subject?: string } }> = meta.sections || [];
  // Separate priority section from email sections
  const emailSections = sections.filter(s => !s.heading.startsWith('⚡'));
  const prioritySection = sections.find(s => s.heading.startsWith('⚡'));

  /** Build a styled A4 HTML document and trigger browser print → Save as PDF */
  const handleDownload = () => {
    const sectionsHtml = emailSections.map(s =>
      `<div style="margin-bottom:20px;border-left:3px solid #818CF8;padding-left:12px;">
        <h3 style="font-size:13px;font-weight:600;color:#1E293B;margin:0 0 4px 0;">${s.heading}</h3>
        <p style="font-size:11px;line-height:1.7;color:#475569;margin:0;">${s.content}</p>
      </div>`
    ).join('');

    const priorityHtml = prioritySection
      ? `<div style="margin-top:32px;padding:16px;background:#FEF3C7;border-radius:8px;border:1px solid #FDE68A;">
          <h3 style="font-size:13px;font-weight:700;color:#92400E;margin:0 0 8px 0;">⚡ Priority Items</h3>
          <p style="font-size:11px;line-height:1.8;color:#78350F;margin:0;white-space:pre-line;">${prioritySection.content}</p>
        </div>` : '';

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${meta.title || 'Document'}</title>
  <style>
    @import url('/fonts/fonts.css');
    @page { size: A4; margin: 20mm 25mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', system-ui, sans-serif; color: #1E293B; }
    .cover {
      width: 100%; min-height: 100vh; display: flex; flex-direction: column;
      justify-content: center; align-items: center; text-align: center;
      background: linear-gradient(135deg, #0F172A 0%, #1E293B 40%, #312E81 100%);
      color: white; padding: 60px; page-break-after: always;
    }
    .cover h1 { font-size: 36px; font-weight: 800; letter-spacing: -1px; margin-bottom: 12px; }
    .cover p { font-size: 14px; color: rgba(255,255,255,0.6); max-width: 400px; }
    .cover .meta { margin-top: 40px; font-size: 11px; color: rgba(255,255,255,0.4); }
    .content { padding: 0; }
    .header-bar { background: #EEF2FF; border-bottom: 1px solid #C7D2FE; padding: 10px 0;
      font-size: 9px; font-weight: 600; color: #4338CA; letter-spacing: 1.5px;
      text-transform: uppercase; text-align: center; margin-bottom: 30px; }
    .doc-title { font-size: 22px; font-weight: 700; color: #0F172A; margin-bottom: 6px; }
    .doc-summary { font-size: 12px; color: #64748B; font-style: italic; margin-bottom: 24px; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #E2E8F0;
      display: flex; justify-content: space-between; font-size: 9px; color: #94A3B8; }
    .footer strong { color: #1E293B; }
  </style>
</head>
<body>
  <div class="cover">
    <div style="font-size:36px;font-weight:800;letter-spacing:-2px;margin-bottom:30px;">
      HumanReplica®<span style="color:#818CF8;">.ai</span>
    </div>
    <h1>${meta.title || 'Document'}</h1>
    ${meta.summary ? `<p>${meta.summary}</p>` : ''}
    <div class="meta">Generated by HumanReplica® &ensp;•&ensp; ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
  </div>
  <div class="content">
    <div class="header-bar">HumanReplica.ai® — ${meta.title || 'Document'}</div>
    <h2 class="doc-title">${meta.title || 'Document'}</h2>
    ${meta.summary ? `<p class="doc-summary">${meta.summary}</p>` : ''}
    ${sectionsHtml}
    ${priorityHtml}
    <div class="footer">
      <div><strong>HumanReplica SAS</strong></div>
      <div>${new Date().toLocaleDateString()}</div>
    </div>
  </div>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 500);
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-white/[0.08] overflow-hidden" style={{ background: '#1a1a1a' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center flex-shrink-0 text-base">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-hig-text-primary truncate">{meta.title}</p>
          <p className="text-[11px] text-hig-text-tertiary">{label} • {emailSections.length} emails</p>
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 hover:text-indigo-200 transition-all text-[11px] font-medium"
          title="Download as PDF"
        >
          <Download size={13} />
          Download
        </button>
        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-400">✓ Generated</span>
      </div>

      {/* Overview summary */}
      {meta.summary && (
        <div className="px-4 py-2 border-b border-chat-border-subtle">
          <p className="text-[12px] text-hig-text-tertiary italic">{meta.summary}</p>
        </div>
      )}

      {/* Email sections — always visible */}
      {emailSections.length > 0 && (
        <div className="divide-y divide-chat-border-subtle">
          {emailSections.map((section, i) => (
            <div key={i} className="px-4 py-3 group">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-semibold text-hig-text-primary mb-1 truncate">{section.heading}</p>
                  <p className="text-[12px] text-hig-text-secondary leading-relaxed">{section.content}</p>
                </div>
                {onDraftReply && section.emailMeta && (
                  <button
                    onClick={() => onDraftReply(
                      section.emailMeta!.subject || section.heading,
                      section.emailMeta!.from || '',
                      section.emailMeta!.messageId
                    )}
                    className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md bg-hig-accent/10 text-hig-accent hover:bg-hig-accent/20 hover:text-hig-accent transition-all text-[10px] font-medium opacity-60 group-hover:opacity-100"
                    title="Draft a reply to this email"
                  >
                    <Mail size={11} />
                    Draft Reply
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Priority items */}
      {prioritySection && (
        <div className="px-4 py-3 border-t border-amber-500/20 bg-amber-500/5">
          <p className="text-[11px] font-semibold text-amber-400 mb-1">{prioritySection.heading}</p>
          <p className="text-[11px] text-amber-200/70 leading-relaxed whitespace-pre-line">{prioritySection.content}</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ACTION RESULT CARD (Email, Calendar, Task, Reminder)
// ═══════════════════════════════════════════════════════════════════════════════

function ActionResultCard({ meta }: { meta: any }) {
  const typeConfig: Record<string, { icon: string; label: string; color: string }> = {
    email_drafted: { icon: '📧', label: 'Email Draft', color: 'from-blue-500 to-cyan-600' },
    calendar_event: { icon: '📅', label: 'Calendar Event', color: 'from-emerald-500 to-teal-600' },
    calendar_list:    { icon: '📅', label: 'Upcoming Events', color: 'from-emerald-500 to-teal-600' },
    calendar_created: { icon: '✅', label: 'Event Added', color: 'from-emerald-500 to-green-600' },
    task_created: { icon: '✅', label: 'Task Created', color: 'from-amber-500 to-orange-600' },
    task_list: { icon: '📋', label: 'Tasks', color: 'from-amber-500 to-orange-600' },
    reminder_set: { icon: '🔔', label: 'Reminder Set', color: 'from-purple-500 to-pink-600' },
  };

  const config = typeConfig[meta.type] || { icon: '⚡', label: 'Action', color: 'from-gray-500 to-gray-600' };

  return (
    <div className="mt-3 rounded-xl border border-white/[0.08] overflow-hidden" style={{ background: '#1a1a1a' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.color} flex items-center justify-center flex-shrink-0 text-base`}>
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-hig-text-primary truncate">{config.label}</p>
          <p className="text-[11px] text-hig-text-tertiary">
            {meta.type === 'email_drafted' && `To: ${meta.to || 'Not specified'} • ${meta.subject || 'No subject'}`}
            {meta.type === 'calendar_event' && `${meta.title || 'Event'}${meta.date ? ` • ${meta.date}` : ''}${meta.time ? ` at ${meta.time}` : ''}`}
            {meta.type === 'calendar_list' && `${meta.events?.length ?? 0} event${meta.events?.length !== 1 ? 's' : ''} in the next 7 days`}
            {meta.type === 'calendar_created' && `"${meta.title}"${meta.start ? ` • ${new Date(meta.start).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}`}
            {meta.type === 'task_created' && `${meta.title || 'Task'} • ${meta.priority || 'MEDIUM'} priority`}
            {meta.type === 'task_list' && `${meta.tasks?.length || 0} pending tasks`}
            {meta.type === 'reminder_set' && `${meta.text || 'Reminder'}${meta.time ? ` • ${meta.time}` : ''}`}
          </p>
        </div>
        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-medium">
          <CheckCheck size={12} /> Done
        </span>
      </div>

      {/* Body — type-specific content */}
      {meta.type === 'email_drafted' && meta.body && (
        <div className="px-4 py-3">
          <p className="text-[11px] text-hig-text-tertiary mb-1">Subject: <span className="text-hig-text-primary">{meta.subject || '—'}</span></p>
          <p className="text-[12px] text-hig-text-secondary leading-relaxed whitespace-pre-wrap">{meta.body}</p>
        </div>
      )}

      {meta.type === 'calendar_event' && (
        <div className="px-4 py-3 space-y-1">
          {meta.date && <p className="text-[12px] text-hig-text-secondary">📆 Date: <span className="text-hig-text-primary">{meta.date}</span></p>}
          {meta.time && <p className="text-[12px] text-hig-text-secondary">🕐 Time: <span className="text-hig-text-primary">{meta.time}</span></p>}
          {meta.duration && <p className="text-[12px] text-hig-text-secondary">⏱ Duration: <span className="text-hig-text-primary">{meta.duration}</span></p>}
          {meta.description && <p className="text-[12px] text-hig-text-tertiary mt-2 italic">{meta.description}</p>}
        </div>
      )}

      {/* Calendar list: show event rows */}
      {meta.type === 'calendar_list' && (
        meta.events?.length > 0 ? (
          <div className="divide-y divide-chat-border-subtle">
            {meta.events.map((ev: any, i: number) => (
              <div key={i} className="flex items-start gap-3 px-4 py-2.5">
                <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0 text-xs">📅</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-hig-text-primary truncate">{ev.title || ev.summary || '(No Title)'}</p>
                  {ev.start && (
                    <p className="text-[11px] text-hig-text-tertiary">
                      {new Date(ev.start).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                  {ev.location && <p className="text-[10px] text-hig-text-tertiary truncate">📍 {ev.location}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 py-3">
             <p className="text-[12px] text-hig-text-tertiary italic">No upcoming events in the next 7 days.</p>
          </div>
        )
      )}

      {/* Calendar created confirmation */}
      {meta.type === 'calendar_created' && (
        <div className="px-4 py-3 space-y-1">
          <p className="text-[12px] text-hig-text-secondary">📝 Title: <span className="text-hig-text-primary">{meta.title}</span></p>
          {meta.start && <p className="text-[12px] text-hig-text-secondary">🕐 Start: <span className="text-hig-text-primary">{new Date(meta.start).toLocaleString('en-US', { weekday: 'long', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span></p>}
          {meta.htmlLink && (
            <a href={meta.htmlLink} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1 text-[11px] text-emerald-400 hover:text-emerald-300 transition-colors">
              Open in Google Calendar →
            </a>
          )}
        </div>
      )}

      {meta.type === 'task_list' && meta.tasks?.length > 0 && (
        <div className="divide-y divide-chat-border-subtle">
          {meta.tasks.map((t: any) => (
            <div key={t.id} className="flex items-center gap-2 px-4 py-2">
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${t.priority === 'HIGH' ? 'bg-red-500/20 text-red-300' :
                  t.priority === 'LOW' ? 'bg-gray-500/20 text-gray-400' :
                    'bg-amber-500/20 text-amber-300'
                }`}>{t.priority}</span>
              <span className="text-[12px] text-hig-text-secondary">{t.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

interface ReplicaChatViewProps { replicaId: string; onBack: () => void; onNavigate?: (view: string) => void; }

export default function ReplicaChatView({ replicaId, onBack, onNavigate }: ReplicaChatViewProps) {
  const {
    conversations, messages, isTyping, streamingContent, currentConversation,
    sendMessage, openChat, createNewChat, switchConversation,
    deleteConversation, renameConversation, stopGeneration, error, clearError,
  } = useChat();
  const { getReplicaById, userReplicas: allReplicas } = useReplica();
  const { user } = useAuth();

  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null);
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Chat toolbar preferences (persisted in localStorage)
  const [chatPrefs, updateChatPrefs] = useChatPreferences();

  // Logo lifecycle: listening (textarea focused) → thinking (agent typing) → default
  const logoState: HRLogoState = isTyping ? 'thinking' : isFocused ? 'listening' : 'default';

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fix #7: avoid infinite re-render by stabilizing openChat ref
  const openChatRef = useRef(openChat);
  openChatRef.current = openChat;

  // Resolve replica: try getReplicaById first, then fall back to scanning all replicas
  const replica = getReplicaById(replicaId) || allReplicas.find(r => r.id === replicaId);
  const replicaName = replica?.name
    || currentConversation?.title
    || replicaId.replace(/^replica_\d+_/, '').slice(0, 12)
    || 'Replica';

  const replicaConversations = (conversations || []).filter(c => c.replicaId === replicaId);
  const filteredConversations = searchQuery
    ? replicaConversations.filter(c => (c.title || c.lastMessage || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : replicaConversations;
  const groupedConversations = groupConversations(filteredConversations);
  const isEmpty = (messages || []).length === 0 && !isTyping;

  // Dynamic workflow capabilities for suggestion chips
  const [workflowChips, setWorkflowChips] = useState<Array<{ slug: string; label: string; icon: string; examplePrompt: string }>>([]);

  useEffect(() => {
    const loadCapabilities = async () => {
      try {
        const token = await user?.getIdToken?.();
        const res = await fetch(`${API_BASE}/chat/capabilities`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setWorkflowChips(data.capabilities || []);
        }
      } catch { /* ignore — chips are optional enhancement */ }
    };
    loadCapabilities();
  }, [user]);

  // Open chat on mount — stabilized to avoid infinite loop
  useEffect(() => {
    openChatRef.current(replicaId);
  }, [replicaId]);

  // Auto-scroll to bottom
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, isTyping, streamingContent]);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }, []);
  useEffect(() => { autoResize(); }, [input, autoResize]);

  // ── File handling ──────────────────────────────────────────────────────────
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (!ALLOWED_MIME_TYPES.includes(file.type)) { setFileError(`"${file.type}" not supported.`); continue; }
      if (file.size > 50 * 1024 * 1024) { setFileError(`"${file.name}" exceeds 50 MB.`); continue; }
      const pending: PendingFile = { file };
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => { pending.preview = ev.target?.result as string; setPendingFiles(prev => [...prev]); };
        reader.readAsDataURL(file);
      }
      setPendingFiles(prev => [...prev, pending]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (idx: number) => setPendingFiles(prev => prev.filter((_, i) => i !== idx));

  const uploadFile = async (file: File): Promise<ChatAttachment | null> => {
    try {
      const token = await user?.getIdToken?.();
      const fd = new FormData(); fd.append('file', file); fd.append('type', 'TRAINING_DOCUMENT');
      const res = await fetch(`${API_BASE}/upload/media/${encodeURIComponent(replicaId)}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      return { id: data.mediaAsset?.id || crypto.randomUUID(), filename: file.name, url: data.mediaAsset?.url || '', mimeType: file.type, sizeBytes: file.size };
    } catch {
      // Show warning instead of silently using blob URL
      setFileError(`Failed to upload "${file.name}". File will not be persisted.`);
      return { id: crypto.randomUUID(), filename: file.name, url: URL.createObjectURL(file), mimeType: file.type, sizeBytes: file.size };
    }
  };

  // ── Send ───────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    const hasText = input.trim().length > 0;
    const hasFiles = pendingFiles.length > 0;
    if (!hasText && !hasFiles) return;
    const text = input; const filesToUpload = [...pendingFiles];
    setInput(''); setPendingFiles([]); setFileError(null); clearError?.();
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    let attachments: ChatAttachment[] = [];
    if (filesToUpload.length > 0) {
      setUploading(true);
      const results = await Promise.all(filesToUpload.map(pf => uploadFile(pf.file)));
      attachments = results.filter(Boolean) as ChatAttachment[];
      setUploading(false);
    }

    let messageContent = text;
    if (attachments.length > 0 && !messageContent) messageContent = `[Attached ${attachments.length} file${attachments.length > 1 ? 's' : ''}]`;
    else if (attachments.length > 0) messageContent += ` 📎 ${attachments.length} file${attachments.length > 1 ? 's' : ''}`;

    // Derive chatOptions from toolbar preferences
    const chatOptions = {
      modelTier: getModelTier(chatPrefs.modelId),
      webSearch: chatPrefs.webSearch,
      extendedThinking: chatPrefs.extendedThinking,
    };

    await sendMessage(messageContent, attachments.length > 0 ? attachments : undefined, chatOptions);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleRenameSubmit = async (id: string) => {
    if (renameValue.trim()) await renameConversation(id, renameValue.trim());
    setRenamingId(null); setRenameValue('');
  };

  const handleDeleteConversation = async () => {
    if (!deletingId) return;
    await deleteConversation(deletingId);
    setDeletingId(null);
  };

  const copyMessageContent = (msgId: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedMsgId(msgId);
    setTimeout(() => setCopiedMsgId(null), 2000);
  };

  const suggestedPrompts = [
    { text: 'Explain what you can do', icon: '✨' },
    { text: 'Brainstorm ideas with me', icon: '💡' },
    ...workflowChips.map(c => ({ text: c.examplePrompt, icon: c.icon })),
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="flex h-[calc(100vh-64px)] lg:h-screen overflow-hidden relative bg-chat-bg"
    >
      {/* Delete confirmation dialog */}
      {deletingId && (
        <DeleteConfirmDialog
          onConfirm={handleDeleteConversation}
          onCancel={() => setDeletingId(null)}
        />
      )}

      {/* ── CONVERSATION SIDEBAR (slide-over inside chat area) ──────── */}
      {/* Overlay */}
      {sidebarOpen && (
        <div className="absolute inset-0 z-30 bg-black/40" onClick={() => setSidebarOpen(false)} role="presentation" aria-label="Close sidebar" />
      )}

      {/* Panel */}
      <aside
        className={`
          absolute top-0 left-0 bottom-0 z-40 w-[260px] flex flex-col
          transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ background: 'var(--chat-sidebar-bg)' }}
      >
        {/* New chat + close */}
        <div className="flex items-center justify-between p-3">
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg text-hig-text-secondary hover:text-hig-text-primary hover:bg-chat-hover transition-colors"
          >
            <PanelLeft size={18} />
          </button>
          <button
            onClick={() => { createNewChat(replicaId); setSidebarOpen(false); }}
            className="p-2 rounded-lg text-hig-text-secondary hover:text-hig-text-primary hover:bg-chat-hover transition-colors"
            title="New chat"
            aria-label="New chat"
          >
            <Plus size={18} />
          </button>
        </div>

        {/* Search */}
        <div className="px-3 pb-2">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-chat-hover border border-chat-border-subtle">
            <Search size={14} className="text-hig-text-tertiary" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search chats"
              className="flex-1 bg-transparent text-[13px] text-hig-text-primary placeholder:text-hig-text-tertiary focus:outline-none"
            />
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto px-2 py-1 scrollbar-thin">
          {groupedConversations.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare size={20} className="mx-auto text-hig-text-tertiary mb-2" />
              <p className="text-[12px] text-hig-text-tertiary">No conversations yet</p>
            </div>
          ) : (
            groupedConversations.map(group => (
              <div key={group.label} className="mb-2">
                <p className="px-3 py-2 text-[11px] text-hig-text-tertiary font-medium">{group.label}</p>
                {group.items.map(convo => {
                  const isActive = currentConversation?.id === convo.id;
                  const isRenaming = renamingId === convo.id;
                  return (
                    <div
                      key={convo.id}
                      className={`group relative flex items-center px-3 py-2.5 rounded-lg cursor-pointer transition-colors mb-0.5 ${isActive ? 'bg-chat-hover' : 'hover:bg-chat-hover'
                        }`}
                      style={!isActive && convo.lastMessage ? { background: 'rgba(0,229,255,0.04)' } : undefined}
                      onClick={() => { switchConversation(convo.id); setSidebarOpen(false); }}
                    >
                      {isRenaming ? (
                        <input
                          type="text" value={renameValue} onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleRenameSubmit(convo.id); if (e.key === 'Escape') setRenamingId(null); }}
                          onBlur={() => handleRenameSubmit(convo.id)}
                          className="flex-1 bg-transparent border-b border-hig-border text-[13px] text-hig-text-primary focus:outline-none"
                          autoFocus onClick={e => e.stopPropagation()}
                        />
                      ) : (
                        <span className="flex-1 text-[13px] text-hig-text-primary truncate pr-6">
                          {convo.title || convo.lastMessage || 'New conversation'}
                        </span>
                      )}
                      {!isRenaming && (
                        <div className="hidden group-hover:flex items-center gap-0.5 absolute right-1.5">
                          <button onClick={e => { e.stopPropagation(); setRenamingId(convo.id); setRenameValue(convo.title || convo.lastMessage || ''); }}
                            className="p-1 text-hig-text-tertiary hover:text-hig-text-primary rounded transition-colors" title="Rename">
                            <Edit3 size={12} />
                          </button>
                          <button onClick={e => { e.stopPropagation(); setDeletingId(convo.id); }}
                            className="p-1 text-hig-text-tertiary hover:text-red-400 rounded transition-colors" title="Delete">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </aside>

      {/* ── MAIN CONTENT ───────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 relative">

        {/* Top bar */}
        <div className="flex items-center justify-between px-4 h-12 flex-shrink-0">
          <div className="flex items-center gap-1">
            {/* Back button */}
            <button
              onClick={onBack}
              className="p-2 rounded-lg text-hig-text-secondary hover:text-hig-text-primary hover:bg-chat-hover transition-colors"
              title="Back to messages"
            >
              <ArrowLeft size={18} />
            </button>
            {/* Sidebar toggle — controls conversation history sidebar */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg text-hig-text-secondary hover:text-hig-text-primary hover:bg-chat-hover transition-colors"
              title="Toggle conversation history"
              aria-label="Toggle conversation history"
              aria-expanded={sidebarOpen}
            >
              <PanelLeft size={18} />
            </button>
            {/* New chat */}
            <button
              onClick={() => createNewChat(replicaId)}
              className="p-2 rounded-lg text-hig-text-secondary hover:text-hig-text-primary hover:bg-chat-hover transition-colors"
              title="New chat"
            >
              <Plus size={18} />
            </button>
          </div>

          {/* Replica name — centered */}
          <span className="text-[15px] font-medium text-hig-text-primary">{replicaName}</span>

          {/* Spacer to keep name centered — or Memory button */}
          <div className="flex items-center gap-1 w-[120px] justify-end">
            {onNavigate && (
              <button
                onClick={() => onNavigate(`memory-${replicaId}`)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium text-hig-text-tertiary hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                title="Memory Browser — search past conversations"
              >
                <Brain size={14} />
                <span className="hidden sm:inline">Memory</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Error banner ────────────────────────────────────────────── */}
        {error && (
          <div className="mx-4 mb-2 flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/20 rounded-xl">
            <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
            <p className="text-[13px] text-red-300 flex-1">{error}</p>
            <button onClick={clearError} className="text-red-400 hover:text-red-300 p-1" aria-label="Dismiss error">
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── Chat messages scroll area ──────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[48rem] mx-auto w-full px-4">

            {/* ── Welcome state ──────────────────────────────────────── */}
            {isEmpty && (
              <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <HRLogo
                  state="default"
                  size={40}
                  showGlow={false}
                  className="mb-5 opacity-70"
                />
                <h1 className="text-[28px] font-semibold text-hig-text-primary mb-2">
                  What can I help with?
                </h1>
                <p className="text-[15px] text-hig-text-tertiary mb-8">
                  {replicaName} can handle documents, communication, analysis, and more.
                </p>

                {/* Capability category cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl w-full mb-8">
                  {[
                    {
                      icon: '📝', label: 'Documents',
                      prompt: 'Create a project status report for this week',
                      color: 'var(--accent)',
                    },
                    {
                      icon: '📧', label: 'Communication',
                      prompt: 'Draft a follow-up email after today\'s meeting',
                      color: '#30D158',
                    },
                    {
                      icon: '🔍', label: 'Analysis',
                      prompt: 'Summarize the key trends in our latest data',
                      color: '#BF5AF2',
                    },
                  ].map((cat) => (
                    <button
                      key={cat.label}
                      onClick={() => { setInput(cat.prompt); textareaRef.current?.focus(); }}
                      className="flex flex-col items-start gap-2 p-4 rounded-2xl border border-chat-border bg-chat-hover hover:bg-hig-bg-tertiary hover:border-hig-border transition-all text-left group"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">{cat.icon}</span>
                        <span className="text-[13px] font-medium text-hig-text-primary">{cat.label}</span>
                      </div>
                      <p className="text-[12px] text-hig-text-tertiary group-hover:text-hig-text-secondary transition-colors leading-relaxed">
                        {cat.prompt}
                      </p>
                    </button>
                  ))}
                </div>

                {/* Additional prompts as chips */}
                <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                  {suggestedPrompts.map((p) => (
                    <button
                      key={p.text}
                      onClick={() => { setInput(p.text); textareaRef.current?.focus(); }}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-chat-border text-[13px] text-hig-text-secondary hover:text-hig-text-primary hover:bg-chat-hover hover:border-hig-border transition-all"
                    >
                      <span>{p.icon}</span>
                      <span>{p.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Message list ───────────────────────────────────────── */}
            {messages.map((msg) => {
              const isHuman = msg.senderType === 'human';
              return (
                <div
                  key={msg.id}
                  className="py-5"
                  onMouseEnter={() => setHoveredMsgId(msg.id)}
                  onMouseLeave={() => setHoveredMsgId(null)}
                >
                  {/* User message — right-aligned rounded bubble */}
                  {isHuman ? (
                    <div className="flex justify-end">
                      <div className="max-w-[85%] rounded-3xl px-5 py-3 text-[15px] leading-[1.6] text-hig-text-primary bg-chat-bubble-user"
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        {/* Attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mt-2 space-y-1.5">
                            {msg.attachments.map(att => {
                              const Icon = getFileIcon(att.mimeType);
                              if (att.mimeType.startsWith('image/'))
                                return <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer" className="block max-w-[200px] rounded-xl overflow-hidden mt-2"><img src={att.url} alt={att.filename} className="w-full h-auto" /></a>;
                              return (
                                <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-xl bg-chat-hover mt-1">
                                  <Icon size={14} className="text-hig-accent" />
                                  <span className="text-[12px] text-hig-text-secondary truncate">{att.filename}</span>
                                  <span className="text-[10px] text-hig-text-tertiary">{formatFileSize(att.sizeBytes)}</span>
                                </a>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* AI message — left-aligned, no bubble, just flowing text */
                    <div className="flex gap-4">
                      {/* Avatar — dynamic: reflects current chat lifecycle state */}
                      <HRLogo state={logoState} size={40} showGlow={false} className="flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-[13px] font-semibold text-hig-text-primary">{replicaName}</p>
                          {/* HR-BREV-020: Significance indicator */}
                          {msg.significance && msg.significance.score >= 0.3 && (
                            <div
                              className="group relative flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium transition-all cursor-default"
                              style={{
                                background: msg.significance.shouldArchive
                                  ? 'linear-gradient(135deg, rgba(251,191,36,0.15), rgba(245,158,11,0.1))'
                                  : 'rgba(148,163,184,0.1)',
                                color: msg.significance.shouldArchive ? '#fbbf24' : '#94a3b8',
                                border: `1px solid ${msg.significance.shouldArchive ? 'rgba(251,191,36,0.25)' : 'rgba(148,163,184,0.15)'}`,
                              }}
                              title={`Significance: ${Math.round(msg.significance.score * 100)}% — ${msg.significance.triggers.join(', ')}`}
                            >
                              {msg.significance.shouldArchive ? '⭐' : '◆'}
                              <span className="hidden sm:inline">
                                {msg.significance.shouldArchive ? 'Archived' : 'Notable'}
                              </span>
                              {/* Tooltip on hover */}
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-[#1a1a2e] border border-chat-border text-[11px] text-hig-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl">
                                <div className="font-medium text-hig-text-primary mb-1">
                                  Significance Score: {Math.round(msg.significance.score * 100)}%
                                </div>
                                {msg.significance.triggers.length > 0 && (
                                  <div className="text-[10px]">
                                    Triggers: {msg.significance.triggers.join(', ')}
                                  </div>
                                )}
                                {msg.significance.shouldArchive && (
                                  <div className="text-amber-400 text-[10px] mt-0.5">
                                    ✓ Archived to memory
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="text-[15px] leading-[1.7] text-hig-text-secondary">
                          <MarkdownMessage content={msg.content} />
                        </div>
                        {/* Attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mt-2 space-y-1.5">
                            {msg.attachments.map(att => {
                              const Icon = getFileIcon(att.mimeType);
                              if (att.mimeType.startsWith('image/'))
                                return <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer" className="block max-w-[240px] rounded-xl overflow-hidden mt-2 border border-chat-border-subtle"><img src={att.url} alt={att.filename} className="w-full h-auto" /></a>;
                              return (
                                <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-xl bg-chat-hover border border-chat-border-subtle mt-1 w-fit">
                                  <Icon size={14} className="text-hig-accent" />
                                  <span className="text-[12px] text-hig-text-secondary truncate">{att.filename}</span>
                                  <span className="text-[10px] text-hig-text-tertiary">{formatFileSize(att.sizeBytes)}</span>
                                </a>
                              );
                            })}
                          </div>
                        )}
                        {/* Action buttons on hover */}
                        <div className={`flex items-center gap-1 mt-2 transition-opacity ${hoveredMsgId === msg.id ? 'opacity-100' : 'opacity-0'}`}>
                          <button onClick={() => copyMessageContent(msg.id, msg.content)} className="p-1.5 rounded-md text-hig-text-tertiary hover:text-hig-text-primary hover:bg-chat-hover transition-colors" title="Copy" aria-label="Copy message">
                            {copiedMsgId === msg.id ? <CheckCheck size={14} /> : <Copy size={14} />}
                          </button>
                        </div>
                        {/* Workflow Result Card */}
                        {msg.workflowMeta && msg.workflowMeta.type === 'workflow_result' && (
                          <WorkflowResultCard meta={msg.workflowMeta} />
                        )}
                        {/* Document Result Card */}
                        {(msg as any).documentMeta && (msg as any).documentMeta.type === 'document_generated' && (
                          <DocumentResultCard
                            meta={(msg as any).documentMeta}
                            onDraftReply={(subject, from, messageId) => {
                              const text = messageId
                                ? `Draft a reply to the email with subject "${subject}" from ${from}`
                                : `Draft a reply to "${subject}" from ${from}`;
                              setInput(text);
                              setTimeout(() => textareaRef.current?.focus(), 50);
                            }}
                          />
                        )}
                        {/* Action Result Card (Email, Calendar, Task, Reminder) */}
                        {(msg as any).actionMeta && (
                          <ActionResultCard meta={(msg as any).actionMeta} />
                        )}
                        {/* Workflow Follow-up Suggestion Chips */}
                        {msg.workflowMeta?.suggestions && (msg.workflowMeta.suggestions || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {msg.workflowMeta.suggestions.map(s => (
                              <button
                                key={s}
                                onClick={() => { setInput(s); textareaRef.current?.focus(); }}
                                className="px-3 py-1.5 rounded-full border border-chat-border text-[12px] text-hig-text-secondary hover:text-hig-text-primary hover:bg-chat-hover hover:border-hig-border transition-all"
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* ── Streaming / typing indicator ───────────────────────── */}
            {isTyping && (
              <div className="py-5">
                <div className="flex gap-4">
                  <HRLogo state="thinking" size={40} showGlow={false} className="flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-hig-text-primary mb-2">{replicaName}</p>
                    {streamingContent ? (
                      <div className="text-[15px] leading-[1.7] text-hig-text-secondary">
                        <MarkdownMessage content={streamingContent} />
                        <span className="inline-block w-[3px] h-[18px] bg-white/60 animate-pulse ml-0.5 align-text-bottom rounded-sm" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 py-2">
                        <span className="w-2 h-2 rounded-full bg-hig-text-tertiary animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 rounded-full bg-hig-text-tertiary animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 rounded-full bg-hig-text-tertiary animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} className="h-4" />
          </div>
        </div>

        {/* ── INPUT AREA (ChatGPT style bottom bar) ──────────────────── */}
        <div className="flex-shrink-0 pb-4 pt-2">
          {/* Pending files */}
          {pendingFiles.length > 0 && (
            <div className="max-w-[48rem] mx-auto px-4 mb-2">
              <div className="flex items-center gap-2 overflow-x-auto">
                {pendingFiles.map((pf, idx) => {
                  const Icon = getFileIcon(pf.file.type);
                  return (
                    <div key={idx} className="relative flex-shrink-0 group">
                      {pf.preview ? (
                        <div className="w-14 h-14 rounded-xl overflow-hidden border border-chat-border"><img src={pf.preview} alt={pf.file.name} className="w-full h-full object-cover" /></div>
                      ) : (
                        <div className="w-14 h-14 rounded-xl border border-chat-border bg-chat-hover flex flex-col items-center justify-center gap-0.5">
                          <Icon size={16} className="text-hig-accent" />
                          <span className="text-[8px] text-hig-text-tertiary">{pf.file.name.split('.').pop()?.toUpperCase()}</span>
                        </div>
                      )}
                      <button onClick={() => removePendingFile(idx)} className="absolute -top-1 -right-1 w-4 h-4 bg-[#ef4444] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={10} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {fileError && (
            <div className="max-w-[48rem] mx-auto px-4 mb-2">
              <p className="text-[12px] text-red-400 px-4 py-1.5 bg-red-500/10 rounded-xl border border-red-500/20">{fileError}</p>
            </div>
          )}

          {/* The main input bar */}
          <div className="max-w-[48rem] mx-auto px-4">
            <div
              className="rounded-3xl px-4 py-2.5 border border-chat-border focus-within:border-hig-border transition-colors bg-chat-input"
            >
            <div className="flex items-end gap-1">
              {/* Attach button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="p-2 rounded-lg text-hig-text-tertiary hover:text-hig-text-primary transition-colors disabled:opacity-40 flex-shrink-0"
                title="Attach files"
              >
                <Paperclip size={18} />
              </button>
              <input ref={fileInputRef} type="file" multiple accept={ALLOWED_MIME_TYPES.join(',')} onChange={handleFileSelect} className="hidden" />

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder="Ask anything"
                rows={1}
                className="flex-1 resize-none bg-transparent text-[15px] text-hig-text-primary placeholder:text-hig-text-tertiary focus:outline-none py-2 max-h-[200px] leading-relaxed"
                disabled={uploading}
              />

              {/* Microphone button — speech-to-text via Whisper */}
              <button
                onClick={async () => {
                  // Stop recording if active
                  if (isRecording) {
                    mediaRecorderRef.current?.stop();
                    return;
                  }

                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                      ? 'audio/webm;codecs=opus'
                      : MediaRecorder.isTypeSupported('audio/mp4')
                        ? 'audio/mp4'
                        : 'audio/webm';

                    const mediaRecorder = new MediaRecorder(stream, { mimeType });
                    audioChunksRef.current = [];

                    mediaRecorder.ondataavailable = (event) => {
                      if (event.data.size > 0) audioChunksRef.current.push(event.data);
                    };

                    mediaRecorder.onstop = async () => {
                      // Stop all tracks to release mic
                      stream.getTracks().forEach(track => track.stop());
                      setIsRecording(false);
                      setIsTranscribing(true);

                      try {
                        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                        // Convert to base64
                        const reader = new FileReader();
                        const base64 = await new Promise<string>((resolve, reject) => {
                          reader.onloadend = () => {
                            const result = reader.result as string;
                            resolve(result.split(',')[1]); // Strip data:...;base64, prefix
                          };
                          reader.onerror = reject;
                          reader.readAsDataURL(audioBlob);
                        });

                        // Send to Whisper transcription endpoint
                        const API_BASE = getApiUrl();
                        const token = await user?.getIdToken?.();
                        const res = await fetch(`${API_BASE}/chat/transcribe`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                          },
                          body: JSON.stringify({
                            audio: base64,
                            mimeType: mimeType.split(';')[0], // Clean mime type
                          }),
                        });

                        const data = await res.json();
                        if (data.text) {
                          setInput(prev => prev ? `${prev} ${data.text}` : data.text);
                        } else if (data.error) {
                          setFileError(`Transcription failed: ${data.error}`);
                        }
                      } catch (err) {
                        console.error('Transcription error:', err);
                        setFileError('Failed to transcribe audio. Please try again.');
                      } finally {
                        setIsTranscribing(false);
                        audioChunksRef.current = [];
                      }
                    };

                    mediaRecorderRef.current = mediaRecorder;
                    mediaRecorder.start(250); // Collect data every 250ms
                    setIsRecording(true);
                  } catch (err) {
                    console.error('Microphone access error:', err);
                    setFileError('Microphone access denied. Please allow microphone access in your browser settings.');
                  }
                }}
                disabled={uploading || isTranscribing}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all flex-shrink-0 ${isTranscribing
                  ? 'text-orange-400 animate-pulse cursor-wait'
                  : isRecording
                    ? 'bg-red-500 text-white animate-pulse hover:bg-red-600 ring-2 ring-red-500/30 ring-offset-1 ring-offset-chat-input'
                    : 'text-hig-text-tertiary hover:text-hig-text-primary hover:bg-chat-hover'
                  }`}
                title={isTranscribing ? 'Transcribing...' : isRecording ? 'Stop recording' : 'Voice input'}
              >
                {isTranscribing ? <Loader2 size={16} className="animate-spin" /> : isRecording ? <MicOff size={16} /> : <Mic size={16} />}
              </button>

              {/* Send / Stop button */}
              {isTyping ? (
                <button
                  onClick={stopGeneration}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-black hover:bg-white/90 transition-colors flex-shrink-0"
                  title="Stop generating"
                >
                  <Square size={14} fill="currentColor" />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={(!input.trim() && pendingFiles.length === 0) || uploading}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-hig-text-primary text-hig-bg-primary disabled:bg-hig-text-tertiary disabled:text-hig-text-secondary disabled:cursor-not-allowed hover:opacity-90 transition-colors flex-shrink-0"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUp size={16} strokeWidth={2.5} />}
                </button>
              )}

              {/* Voice Mode button — continuous conversation */}
              <button
                onClick={() => setVoiceModeOpen(true)}
                disabled={uploading || isTranscribing || isRecording}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white hover:from-orange-400 hover:to-red-400 transition-all flex-shrink-0 shadow-lg shadow-orange-500/20 disabled:opacity-40"
                title="Voice conversation mode"
              >
              <Phone size={14} />
              </button>
            </div>

            {/* Chat toolbar — model selector, toggles */}
            <ChatToolbar
              preferences={chatPrefs}
              updatePreferences={updateChatPrefs}
            />
            </div>

            <p className="text-[11px] text-hig-text-tertiary text-center mt-2">
              {replicaName} can make mistakes. Verify important information.
            </p>
          </div>
        </div>
      </div>

      {/* Voice Mode Overlay */}
      {voiceModeOpen && (
        <VoiceMode
          replicaId={replicaId}
          replicaName={replicaName}
          onClose={() => setVoiceModeOpen(false)}
        />
      )}
    </div>
  );
}
